// ─────────────────────────────────────────────────────────────────────────────
// Shared flight utility functions and constants
// Used by SearchFlights.js, Home.js, and any future flight-result views.
// ─────────────────────────────────────────────────────────────────────────────

export const AIRLINE_NAMES = {
  A1: 'A.P.G. Distribution System',
  AA: 'American Airlines',
  AC: 'Air Canada',
  AF: 'Air France',
  AI: 'Air India',
  AM: 'Aeromexico',
  AS: 'Alaska Airlines',
  AY: 'Finnair',
  AZ: 'ITA Airways',
  BA: 'British Airways',
  BR: 'EVA Air',
  CA: 'Air China',
  CI: 'China Airlines',
  CX: 'Cathay Pacific',
  CZ: 'China Southern Airlines',
  DL: 'Delta Air Lines',
  EK: 'Emirates',
  ET: 'Ethiopian Airlines',
  EY: 'Etihad Airways',
  FJ: 'Fiji Airways',
  GA: 'Garuda Indonesia',
  GF: 'Gulf Air',
  HU: 'Hainan Airlines',
  IB: 'Iberia',
  JL: 'Japan Airlines',
  JQ: 'Jetstar Airways',
  KE: 'Korean Air',
  KL: 'KLM Royal Dutch Airlines',
  KU: 'Kuwait Airways',
  LA: 'LATAM Airlines',
  LH: 'Lufthansa',
  LO: 'LOT Polish Airlines',
  LX: 'SWISS',
  MH: 'Malaysia Airlines',
  MU: 'China Eastern Airlines',
  NH: 'All Nippon Airways',
  NZ: 'Air New Zealand',
  OA: 'Olympic Air',
  OD: 'Batik Air Malaysia',
  OS: 'Austrian Airlines',
  OZ: 'Asiana Airlines',
  PR: 'Philippine Airlines',
  QF: 'Qantas',
  QR: 'Qatar Airways',
  RO: 'Tarom',
  SA: 'South African Airways',
  SK: 'Scandinavian Airlines',
  SN: 'Brussels Airlines',
  SQ: 'Singapore Airlines',
  SU: 'Aeroflot',
  SV: 'Saudia',
  TG: 'Thai Airways',
  TK: 'Turkish Airlines',
  TP: 'TAP Air Portugal',
  UL: 'SriLankan Airlines',
  UX: 'Air Europa',
  VA: 'Virgin Australia',
  VJ: 'VietJet Air',
  VN: 'Vietnam Airlines',
  VS: 'Virgin Atlantic',
  WY: 'Oman Air',
  XQ: 'SunExpress',
  ZH: 'Shenzhen Airlines',
  '6E': 'IndiGo',
  '9W': 'Jet Airways',
  GP: 'APG Airlines',
  HR: 'Hahn Air',
};

export const AGGREGATOR_CARRIER_CODES = new Set(['A1', 'GP', 'HR']);
export const BLOCKED_AIRLINE_NAMES = new Set(['A.P.G. Distribution System', 'Duffel Airways', 'Duffel']);
export const BLOCKED_CARRIER_CODES = new Set(['W2']);

export const TIME_FILTER_OPTIONS = [
  { key: 'before6', label: 'Before 6 AM', icon: '☼' },
  { key: 'morning', label: '6 AM - 12 PM', icon: '☀' },
  { key: 'afternoon', label: '12 PM - 6 PM', icon: '☀' },
  { key: 'after6', label: 'After 6 PM', icon: '☾' },
];

export const STOP_FILTER_OPTIONS = [
  { key: 'nonstop', label: 'Non Stop', value: 0 },
  { key: 'onestop', label: '1 Stop', value: 1 },
];

export const parseTimeToMinutes = (timeValue) => {
  const [hh, mm] = String(timeValue || '').split(':').map(Number);
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null;
  return (hh * 60) + mm;
};

export const getHourFromTime = (timeValue) => {
  const [hh] = String(timeValue || '').split(':').map(Number);
  return Number.isFinite(hh) ? hh : null;
};

export const matchesTimeFilter = (timeValue, filterKey) => {
  if (filterKey === 'any') return true;
  const hour = getHourFromTime(timeValue);
  if (hour == null) return false;
  if (filterKey === 'before6') return hour < 6;
  if (filterKey === 'morning') return hour >= 6 && hour < 12;
  if (filterKey === 'afternoon') return hour >= 12 && hour < 18;
  if (filterKey === 'after6') return hour >= 18;
  return true;
};

export const getStopCountFromFlight = (flight) => {
  if (Array.isArray(flight?.segments) && flight.segments.length > 0) {
    return Math.max(flight.segments.length - 1, 0);
  }
  const typeText = String(flight?.type || '').toLowerCase();
  if (typeText.includes('direct') || typeText.includes('non-stop') || typeText.includes('nonstop')) return 0;
  const stopMatch = typeText.match(/(\d+)\s*-?\s*stop/);
  return stopMatch ? Number(stopMatch[1]) : 0;
};

export const isSingleStopSameCarrier = (flight) => {
  const stopCount = getStopCountFromFlight(flight);
  if (stopCount === 0) return true;
  if (stopCount !== 1) return false;
  if (!Array.isArray(flight?.segments) || flight.segments.length < 2) return false;
  const carrierSet = new Set(
    flight.segments
      .map((segment) => String(segment?.carrierCode || '').toUpperCase())
      .filter(Boolean),
  );
  return carrierSet.size === 1;
};

export const parseDurationToMinutes = (durationValue) => {
  const durationText = String(durationValue || '').trim().toLowerCase();
  if (!durationText) return Number.POSITIVE_INFINITY;
  const hoursMatch = durationText.match(/(\d+)\s*h/);
  const minsMatch = durationText.match(/(\d+)\s*m/);
  const hours = Number(hoursMatch?.[1] || 0);
  const mins = Number(minsMatch?.[1] || 0);
  if (!Number.isFinite(hours) || !Number.isFinite(mins)) return Number.POSITIVE_INFINITY;
  return (hours * 60) + mins;
};

export const getCarrierCodeFromFlight = (flight) => {
  const codeFromFlightNumber = String(flight?.code || '')
    .trim()
    .toUpperCase()
    .replace(/[\s-]/g, '')
    .slice(0, 2);
  const segmentCarrierCode = flight?.segments?.[0]?.carrierCode;
  if (segmentCarrierCode) {
    const normalizedSegmentCarrier = String(segmentCarrierCode).toUpperCase();
    if (
      codeFromFlightNumber
      && AIRLINE_NAMES[codeFromFlightNumber]
      && AGGREGATOR_CARRIER_CODES.has(normalizedSegmentCarrier)
    ) {
      return codeFromFlightNumber;
    }
    return normalizedSegmentCarrier;
  }
  const code = String(flight?.code || '').trim().toUpperCase();
  if (!code) return '';
  const compactCode = code.replace(/[\s-]/g, '');
  const carrierMatch = compactCode.match(/^([A-Z0-9]{2})/);
  return carrierMatch ? carrierMatch[1] : '';
};

export const getFlightAirlineName = (flight) => {
  const airlineName = String(flight?.airline || '').trim();
  const normalizedAirlineCode = airlineName.toUpperCase();
  const codeCarrier = String(flight?.code || '')
    .trim()
    .toUpperCase()
    .replace(/[\s-]/g, '')
    .slice(0, 2);
  const carrierCode = getCarrierCodeFromFlight(flight);
  if (codeCarrier && AIRLINE_NAMES[codeCarrier] && !AGGREGATOR_CARRIER_CODES.has(codeCarrier)) {
    return AIRLINE_NAMES[codeCarrier];
  }
  if (carrierCode && AIRLINE_NAMES[carrierCode]) return AIRLINE_NAMES[carrierCode];
  if (normalizedAirlineCode.length <= 3 && AIRLINE_NAMES[normalizedAirlineCode]) {
    return AIRLINE_NAMES[normalizedAirlineCode];
  }
  if (airlineName && airlineName.length > 3) return airlineName;
  if (airlineName && airlineName.length <= 3) {
    const resolvedName = AIRLINE_NAMES[normalizedAirlineCode];
    if (resolvedName) return resolvedName;
    return airlineName.length === 2 ? `Airline ${airlineName}` : airlineName;
  }
  return carrierCode ? `Airline ${carrierCode}` : 'Airline';
};

export const isBlockedAirlineFlight = (flight) => BLOCKED_AIRLINE_NAMES.has(getFlightAirlineName(flight));

export const getAirlineInitials = (airlineName) => {
  const words = String(airlineName || '').split(/\s+/).filter(Boolean);
  if (words.length === 0) return 'FL';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return `${words[0][0] || ''}${words[1][0] || ''}`.toUpperCase();
};

export const getStopLabel = (stopCode) => {
  const airportCode = String(stopCode || '').trim().toUpperCase();
  if (!airportCode) return 'Unknown stop';
  const AIRPORT_LABELS = {
    SGN: 'Ho Chi Minh City, Vietnam',
    KUL: 'Kuala Lumpur, Malaysia',
    BKK: 'Bangkok, Thailand',
    CMB: 'Colombo, Sri Lanka',
    SIN: 'Singapore, Singapore',
    DOH: 'Doha, Qatar',
    DXB: 'Dubai, United Arab Emirates',
    AUH: 'Abu Dhabi, United Arab Emirates',
  };
  return AIRPORT_LABELS[airportCode] || airportCode;
};

/**
 * Derives a stable grouping key for a flight so that multiple fare offers
 * for the same physical flight (same carrier, same flight number, same departure)
 * are collapsed into a single card with a fare picker.
 * Handles both SearchFlights (departureTime) and Home/enriched (departure) field names.
 */
export const getFlightGroupKey = (flight) => {
  const segs = flight?.segments || [];
  const carrier = (segs[0]?.carrierCode || String(flight?.code || '').split('-')[0] || '').toUpperCase();
  const num = (segs[0]?.flightNumber || String(flight?.code || '').split('-')[1] || '').replace(/\s/g, '');
  const date = String(flight?.date || '');
  const dep = String(flight?.departure || flight?.departureTime || '');
  if (!carrier || carrier === 'FL' || !num) {
    return flight?.code || Math.random().toString(36);
  }
  return `${carrier}-${num}-${date}-${dep}`;
};

export const formatShortDate = (dateValue) =>
  new Date(dateValue).toLocaleDateString('en-AU', { month: 'short', day: 'numeric' });

export const getArrivalDateLabel = (baseDate, departureTime, arrivalTime) => {
  const departureMinutes = parseTimeToMinutes(departureTime);
  const arrivalMinutes = parseTimeToMinutes(arrivalTime);
  if (departureMinutes == null || arrivalMinutes == null) return formatShortDate(baseDate);
  const arrivalDate = new Date(`${baseDate}T00:00:00`);
  if (Number.isNaN(arrivalDate.getTime())) return formatShortDate(baseDate);
  if (arrivalMinutes < departureMinutes) arrivalDate.setDate(arrivalDate.getDate() + 1);
  return arrivalDate.toLocaleDateString('en-AU', { month: 'short', day: 'numeric' });
};

/**
 * Assigns smart tags ('Cheapest', 'Fastest', 'Best Value') to flights.
 * 
 * Logic:
 * - Cheapest: Lowest price. If multiple flights have the same lowest price, pick the one with shortest duration.
 * - Fastest: Shortest duration.
 * - A flight CAN have both Cheapest and Fastest tags if it's the best in both categories.
 * - Best Value: Among remaining flights (excluding those with Cheapest or Fastest tags), uses a scoring algorithm:
 *   score = price + (stops × 180) + (duration_minutes / 4)
 *   The flight with the lowest score is the "Best Value".
 * 
 * Returns a Map: flightCode -> array of badge strings
 * 
 * @param {Array} flights - Array of flight objects with price, duration, and code properties
 * @param {Function} getPriceFunc - Function to extract numeric price from a flight (e.g., f => f.price or f => f.displayPriceAmount)
 * @returns {Map<string, string[]>} Map of flight codes to arrays of their badge labels
 */
export const assignFlightTags = (flights, getPriceFunc = (f) => Number(f.price || 0)) => {
  const badges = new Map();
  
  if (!flights || flights.length === 0) return badges;

  // Find cheapest flight(s)
  const sortedByPrice = [...flights].sort((a, b) => {
    const priceA = getPriceFunc(a);
    const priceB = getPriceFunc(b);
    if (priceA !== priceB) return priceA - priceB;
    // If prices are equal, prefer shorter duration
    return parseDurationToMinutes(a.duration) - parseDurationToMinutes(b.duration);
  });
  
  const cheapest = sortedByPrice[0];
  if (cheapest) {
    badges.set(cheapest.code, ['Cheapest']);
  }

  // Find fastest flight
  const sortedByDuration = [...flights].sort(
    (a, b) => parseDurationToMinutes(a.duration) - parseDurationToMinutes(b.duration)
  );
  
  const fastest = sortedByDuration[0];
  if (fastest) {
    // If it's also the cheapest, add Fastest to the existing array
    if (fastest.code === cheapest?.code) {
      const existing = badges.get(fastest.code) || [];
      badges.set(fastest.code, [...existing, 'Fastest']);
    } else {
      badges.set(fastest.code, ['Fastest']);
    }
  }

  // Find best value (exclude flights that already have tags)
  const taggedCodes = new Set(badges.keys());
  const remainingFlights = flights.filter((f) => !taggedCodes.has(f.code));

  if (remainingFlights.length > 0) {
    const sortedByValue = [...remainingFlights].sort((a, b) => {
      const priceA = getPriceFunc(a);
      const priceB = getPriceFunc(b);
      const stopPenaltyA = getStopCountFromFlight(a) * 180;
      const stopPenaltyB = getStopCountFromFlight(b) * 180;
      const durationA = parseDurationToMinutes(a.duration);
      const durationB = parseDurationToMinutes(b.duration);
      
      const scoreA = priceA + stopPenaltyA + (durationA / 4);
      const scoreB = priceB + stopPenaltyB + (durationB / 4);
      
      return scoreA - scoreB;
    });

    const bestValue = sortedByValue[0];
    if (bestValue) {
      badges.set(bestValue.code, ['Best Value']);
    }
  }

  return badges;
};
